import { Button, ButtonLink } from '@/components/ui/button';
import { MailCheck } from 'lucide-react';
import { useState  } from 'react';
import type {FormEvent} from 'react';
import { Link } from 'react-router-dom';
import { Page } from '@/components/layout/page';
import { DevNote } from '@/components/ui/dev-note';
import { Input } from '@/components/ui/field';
import { Alert, Card, CardBody, Container, Section } from '@/components/ui/primitives';
import { dashlite } from '@/lib/dashlite';
import { fieldErrors, presentError } from '@/lib/errors';
import { paths } from '@/lib/paths';

/**
 * Mot de passe oublié — `POST /auth/forgot-password`.
 *
 * Deux points à respecter :
 *
 * 1. **La confirmation est neutre.** On n'indique jamais si l'adresse est
 *    connue : sinon, le formulaire deviendrait un moyen de vérifier qui a un
 *    compte sur le site. Le message affiché est le même dans les deux cas.
 *
 * 2. **Le lien pointe vers votre front.** L'email envoyé par la plateforme
 *    contient une URL construite à partir de l'adresse de front configurée
 *    pour le site, avec le token et l'email en paramètres. Elle doit
 *    correspondre à la route `/reinitialiser-mot-de-passe` de ce site.
 *
 * Cette page sert aussi aux clients nés d'un paiement invité, qui n'ont
 * jamais choisi de mot de passe : le parcours est le même.
 */
export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState<unknown>(null);

    const fields = fieldErrors(error);

    async function handleSubmit(event: FormEvent): Promise<void> {
        event.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            await dashlite.forgotPassword(email);
            setSent(true);
        } catch (caught) {
            setError(caught);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Page title="Mot de passe oublié">
            <Section>
                <Container className="max-w-md space-y-6">
                    <div className="text-center">
                        <h1 className="font-display text-3xl tracking-tight">Définir un mot de passe</h1>
                        <p className="text-ink-soft mt-2 text-sm">
                            Que vous l'ayez oublié ou jamais choisi, la démarche est la même.
                        </p>
                    </div>

                    {sent ? (
                        <>
                            <Alert tone="positive" title="Vérifiez votre boîte de réception">
                                <p className="flex items-start gap-2">
                                    <MailCheck className="mt-0.5 size-4 shrink-0" aria-hidden />
                                    <span>
                                        Si un compte existe pour cette adresse, un lien de réinitialisation vient d'y
                                        être envoyé. Il expire au bout d'un moment — demandez-en un nouveau s'il ne
                                        fonctionne plus.
                                    </span>
                                </p>
                            </Alert>

                            <div className="flex justify-center gap-3">
                                <ButtonLink to={paths.login} variant="secondary">
                                    Retour à la connexion
                                </ButtonLink>
                                <Button variant="ghost" onClick={() => setSent(false)}>
                                    Renvoyer
                                </Button>
                            </div>
                        </>
                    ) : (
                        <Card>
                            <CardBody>
                                <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                                    <Input
                                        data-dashlite="password-reset-email"
                                        label="Adresse email"
                                        type="email"
                                        required
                                        autoFocus
                                        autoComplete="email"
                                        value={email}
                                        error={fields.email}
                                        onChange={(event) => setEmail(event.target.value)}
                                        hint="Celle utilisée lors de votre achat."
                                    />

                                    {error !== null && Object.keys(fields).length === 0 && (
                                        <Alert tone="critical" title={presentError(error).message}>
                                            {presentError(error).hint}
                                        </Alert>
                                    )}

                                    <Button
                                        data-dashlite="password-reset-submit"
                                        type="submit"
                                        size="lg"
                                        className="w-full"
                                        loading={submitting}
                                    >
                                        Recevoir le lien
                                    </Button>
                                </form>
                            </CardBody>
                        </Card>
                    )}

                    <p className="text-ink-muted text-center text-sm">
                        <Link to={paths.login} className="text-brand-700 hover:underline">
                            Retour à la connexion
                        </Link>
                    </p>

                    <DevNote title="Ce que fait cet écran" endpoints={['POST /auth/forgot-password']}>
                        <p>
                            La réponse est identique que l'adresse soit connue ou non. Affichez donc toujours la même
                            confirmation : distinguer les deux cas transformerait ce formulaire en outil d'énumération
                            de comptes.
                        </p>
                        <p>
                            Le lien envoyé par email pointe vers l'URL de front configurée pour le site, avec le token
                            et l'adresse en paramètres — ici,{' '}
                            <code className="font-mono">{paths.resetPassword}?token=…&amp;email=…</code>.
                        </p>
                    </DevNote>
                </Container>
            </Section>
        </Page>
    );
}
